import type { DotGridColorTheme } from "@/components/backgrounds/dot-grid-types";
import type { DotGridAnimationMode } from "@/components/backgrounds/dot-grid-patterns";
import type { OnboardingStepSlug } from "@/routes/ona-onboarding/onboarding-state";
import { ONBOARDING_STEP_SLUG } from "@/routes/ona-onboarding/onboarding-state";

/**
 * Per-step color theme for the onboarding right panel.
 *
 * Light mode: `bg` sets the panel background directly, dots use full-color values.
 * Dark mode: panel keeps bg-surface-base, `overlay` layers a tinted color at low
 * opacity on top, dots use muted values that read well against the dark base.
 */
export type OnboardingStepColorTheme = {
    /** Direct background color (light mode). Ignored in dark mode when overlay is set. */
    bg: string;
    /** Tinted overlay for dark mode: the hue color + opacity layered over bg-surface-base. */
    overlay?: { color: string; opacity: number };
    dots: DotGridColorTheme;
    /** Animation pattern for the dot grid on this step. */
    animationMode: DotGridAnimationMode;
};

type StepThemeVariants = {
    light: OnboardingStepColorTheme;
    dark: OnboardingStepColorTheme;
};

export const STEP_COLOR_THEMES: Record<OnboardingStepSlug, StepThemeVariants> = {
    [ONBOARDING_STEP_SLUG.connectToScm]: {
        light: {
            bg: "rgb(232, 250, 230)",
            dots: { base: [167, 247, 161], bright: [11, 211, 0] },
            animationMode: "agents",
        },
        dark: {
            bg: "rgb(var(--surface-base))",
            overlay: { color: "rgb(11, 211, 0)", opacity: 0.03 },
            dots: { base: [17, 70, 16], bright: [3, 108, 0] },
            animationMode: "agents",
        },
    },
    [ONBOARDING_STEP_SLUG.selectRepository]: {
        light: {
            bg: "rgb(245, 232, 252)",
            dots: { base: [239, 212, 255], bright: [202, 110, 255] },
            animationMode: "orchestration",
        },
        dark: {
            bg: "rgb(var(--surface-base))",
            overlay: { color: "rgb(182, 61, 252)", opacity: 0.03 },
            dots: { base: [72, 17, 104], bright: [112, 2, 176] },
            animationMode: "orchestration",
        },
    },
    [ONBOARDING_STEP_SLUG.getStartedWithTasks]: {
        light: {
            bg: "rgb(235, 240, 252)",
            dots: { base: [202, 215, 250], bright: [94, 138, 253] },
            animationMode: "governance",
        },
        dark: {
            bg: "rgb(var(--surface-base))",
            overlay: { color: "rgb(47, 105, 253)", opacity: 0.03 },
            dots: { base: [17, 42, 104], bright: [2, 52, 176] },
            animationMode: "governance",
        },
    },
};
